import { lazy, Suspense } from 'react'

export interface MarkdownEditorProps {
    /**
     * Initial markdown content. Treated as **initial-only** — it is parsed
     * into blocks once on mount and never re-synced on subsequent renders
     * (re-syncing on every keystroke would fight the user's cursor). Remount
     * the component via a `key` change when the underlying entity changes.
     */
    value: string
    /** Called with the freshly-exported markdown after a real user edit. */
    onChange: (markdown: string) => void
    onBlur?: () => void
    /**
     * Called when the user presses Escape inside the editor. The event's
     * native propagation to `document` is stopped regardless (so it never
     * closes an ancestor Radix `Sheet`), independently of whether this prop
     * is provided.
     */
    onEscape?: () => void
    placeholder?: string
    autoFocus?: boolean
    /** Tailwind min-height class applied to the editor root, e.g. `'min-h-[200px]'`. */
    minHeight?: string
    'data-testid'?: string
}

// BlockNote is a heavy bundle and `/issues` is far hotter than the Pages
// route (the only other BlockNote consumer today), so the implementation is
// code-split behind the edit state rather than loaded eagerly with the
// issues bundle.
const MarkdownEditorInner = lazy(() => import('./MarkdownEditorInner'))

export function MarkdownEditor(props: MarkdownEditorProps) {
    const minHeight = props.minHeight ?? 'min-h-[120px]'
    return (
        <Suspense
            fallback={
                <div
                    className={`${minHeight} rounded-md border border-input bg-background animate-pulse`}
                    data-testid={props['data-testid']}
                />
            }
        >
            <MarkdownEditorInner {...props} minHeight={minHeight} />
        </Suspense>
    )
}
